import { useContext, useEffect, useState, useRef, KeyboardEvent } from 'react';
import { useParams } from 'react-router';
import { Box, Tab, Tabs, Typography, styled, useTheme, Button, IconButton } from '@mui/material';
import { AuthContext, IUser, Ico_Export, Ico_Eye } from '@sagecentral/core-ui';
import { IProject, IProjectOverview } from 'models/projects';
import { TaskFilter } from 'models/tasks';
import { useProjectWorkItemsWorkflowsExport } from 'hooks/projects';
import ProjectsService from 'services/projects/projects-service';
import WorkItemsWorkflows from './WorkitemsWorkflows';

const TaskFilterTabs = styled(Tabs)(({ theme }) => ({
    color: '#333333',
    '& .MuiTabs-indicator': {
        color: theme.palette.secondary.main,
        backgroundColor: theme.palette.secondary.main,
        height: '1px',
        borderRadius: '4px',
    },
    '& .MuiTabs-flexContainer': {
        outline: 'none',
        padding: 3
    }
}));

const TaskFilterTab = styled(Tab)(({ theme }) => ({
    borderBottom: 1,
    textTransform: 'none',
    '&.Mui-selected': {
        borderBottom: 1,
        fontWeight: 600,
    },
    '&.Mui-focusVisible': {
        outline: '2px solid',
        outlineColor: theme.palette.primary.main,
        outlineOffset: '2px'
    },
    '& .MuiTouchRipple-root': {
        display: 'none'
    }
}));

const WorkItemsWokflowsTabs = () => {
    const auth = useContext(AuthContext);
    const theme = useTheme();

    const { projectId } = useParams();
    const [project, setProject] = useState<IProject | IProjectOverview>();
    const [taskFilter, setTaskFilter] = useState<TaskFilter>('all');
    const [showStatus, setShowStatus] = useState<boolean>(true);
    const [showProgress, setShowProgress] = useState<boolean>(false);
    const [showAll, setShowAll] = useState<boolean>(false);
    
    const [focusedTab, setFocusedTab] = useState<number>(0);
    
    const tabRefs = [
        useRef<HTMLDivElement>(null),
        useRef<HTMLDivElement>(null),
        useRef<HTMLDivElement>(null)
    ];

    const [user, setUser] = useState<IUser>({} as IUser);

    useEffect(() => {
        const readUser = async (): Promise<void> => {
            const user = await auth.getUser() as IUser;
            setUser(user);
        };

        readUser();
    }, [auth]);

    useEffect(() => {
        (async (): Promise<void> => {
            if (user && user.id_token && user.id_token.length > 0) {
                const project = await ProjectsService.getProjectOverview(projectId, user.id_token);
                setProject(project);
            }
        })();
    }, [user, projectId]);

    useEffect(() => {
        setShowStatus(taskFilter === 'all');
        setShowProgress(taskFilter === 'in-progress');
        setShowAll(false);
    }, [taskFilter]);

    const handleExport = useProjectWorkItemsWorkflowsExport(projectId, taskFilter);
    
    const handleTabChange = (_event: React.SyntheticEvent, newValue: TaskFilter): void => {
        setTaskFilter(newValue);
    };
    
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, callback: () => void): void => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            callback();
        }
    };
    
    const tabFilterValues: readonly TaskFilter[] = ['all', 'in-progress', 'completed'] as const;
    
    
    const handleTabsKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
        const tabCount = tabFilterValues.length;
        
        if (event.key === 'Tab') {
            if (!event.shiftKey) {
                const nextTab = focusedTab + 1;
                
                if (nextTab >= tabCount) {
                    return;
                }
                
                event.preventDefault();
                setFocusedTab(nextTab);
                tabRefs[nextTab].current?.focus();
            } else {
                const prevTab = focusedTab - 1;
                
                if (prevTab < 0) {
                    return;
                }
                
                event.preventDefault();
                setFocusedTab(prevTab);
                tabRefs[prevTab].current?.focus();
            }
        } else if ((event.key === 'Enter' || event.key === ' ') && focusedTab >= 0) {
            event.preventDefault();
            setTaskFilter(tabFilterValues[focusedTab]);
        }
    };
    


    return (
        <>
            <Box sx={{
                display: 'inline-block',
                mb: '24px'
            }}>
                <TaskFilterTabs 
                    value={taskFilter} 
                    onChange={handleTabChange}
                    aria-label="Filter tabs for work item status"
                    onKeyDown={handleTabsKeyDown}
                    TabIndicatorProps={{
                        'aria-hidden': true
                    }}
                >
                    <TaskFilterTab
                        label="All Work Items"
                        value="all"
                        id="all-workitems-tab"
                        aria-controls="all-workitems-tabpanel"
                        ref={tabRefs[0]}
                        aria-selected={taskFilter === 'all'}
                        tabIndex={focusedTab === 0 ? 0 : -1}
                        onFocus={(): void => setFocusedTab(0)}
                    />
                    <TaskFilterTab
                        label="Work Items in Progress"
                        value="in-progress"
                        id="in-progress-workitems-tab"
                        aria-controls="in-progress-workitems-tabpanel"
                        ref={tabRefs[1]}
                        aria-selected={taskFilter === 'in-progress'}
                        tabIndex={focusedTab === 1 ? 0 : -1}
                        onFocus={(): void => setFocusedTab(1)}
                    />
                    <TaskFilterTab
                        label="Completed Work Items"
                        value="completed"
                        id="completed-workitems-tab"
                        aria-controls="completed-workitems-tabpanel"
                        ref={tabRefs[2]}
                        aria-selected={taskFilter === 'completed'}
                        tabIndex={focusedTab === 2 ? 0 : -1}
                        onFocus={(): void => setFocusedTab(2)}
                    />
                </TaskFilterTabs>
                <Box sx={{ borderBottom: '1.5px solid', borderColor: 'divider', mt: '-1px' }}></Box>
            </Box>
            <Box sx={{ display: 'flex', float: 'right', mt: '12px', gap: '24px' }}>
                <Button
                    variant="text"
                    onClick={handleExport}
                    startIcon={<svg fill="none" height={24} width={24}>{Ico_Export}</svg>}
                    aria-label="Export work items"
                    sx={{ 
                        fontSize: '14px', 
                        color: theme.palette.common.black,
                        textTransform: 'none'
                    }}
                >
                    Export
                </Button>
                <Button
                    variant="text"
                    onClick={(): void => { setShowAll(!showAll); }}
                    startIcon={<>{Ico_Eye}</>}
                    aria-label={showAll ? "Collapse all work items" : "Show all work items"}
                    sx={{ 
                        fontSize: '14px', 
                        color: theme.palette.common.black,
                        textTransform: 'none'
                    }}
                >
                    {!showAll ? "Show all" : "Collapse all"}
                </Button>
            </Box>
            <div 
                role="tabpanel" 
                id={`${taskFilter}-workitems-tabpanel`}
                aria-labelledby={`${taskFilter}-workitems-tab`}
            >
                <WorkItemsWorkflows
                    statusFilter={taskFilter}
                    showStatus={showStatus}
                    showProgress={showProgress}
                    showUser={false}
                    projectId={project?.id ?? 0}
                    showAll={showAll}
                />
            </div>
        </>
    )
};

export default WorkItemsWokflowsTabs;